import { atom } from 'jotai';
import {
  AbyssViewerCallbacks,
  CameraTargetProps,
  CurrentImageProps,
  VisibilityBoxProps,
} from '@abyss/3d-viewer';

import {
  GoverningValueBlister,
  PointOfInterest,
  AlarmLevels,
  GetAssemblyDetailsQuery,
  BlisterHeightType,
  GetAllAssembliesForEvaluationQuery,
} from '@/__generated__/graphql';
import {
  GetAllDecksQuery,
  GetStructureLocationsForInspectionDataLoaderQuery,
  GetStructureForInspectionDataLoaderQuery,
  LocationLayer,
} from '@/__generated__/graphql';
import {
  Point3d,
  SnackbarMessage,
  Viewpoint,
  ToggleableDefectLegend,
  AllPointOfInterestTemplate,
  AnalysisDrawerRight,
  AnalysisDrawerLeft,
  AssemblyTemplateQuery,
  TemplateWithPOIs,
  AnalysisStructureRelationships,
  AssemblyTemplatesQuery,
  BlendOpacityModes,
  PaintRegionGroup,
  Filters,
  LastDeeplinkParameters,
  AssemblyCameraPose,
} from '@/types';
import { DEFAULT_VISIBILITY_BOX } from '@/constants';
import {
  StructureForRiskAssessmentQuery,
  AllAssemblyTemplatesForRiskAssessmentQuery,
  GetTemplateByTypeQuery,
} from '@/__generated__/graphql';
import { UnitSystemEnum } from '@/__generated__/graphql';
import { ReportDetailsQuery, AllReportsListQuery } from '@/__generated__/graphql';
import { GetStructureFeaturesQuery } from '@/__generated__/graphql';
import { Message } from 'ai';

export const selectedStructureRiskAssessment = atom<
  StructureForRiskAssessmentQuery['structure'] | undefined
>(undefined);

export const inspectionMetadata = atom<
  GetStructureForInspectionDataLoaderQuery['structure'] | undefined
>(undefined);

export const partLegends = atom<{ [key: string]: string }>({});

export const structureLocationsMap = atom<
  Map<string, GetStructureLocationsForInspectionDataLoaderQuery['allLocations'][0]> | undefined
>(undefined);

export const structureLocations = atom<
  GetStructureLocationsForInspectionDataLoaderQuery['allLocations'] | undefined
>(undefined);

export const imageLayer = atom<LocationLayer | undefined>(undefined);

export const structureDecks = atom<GetAllDecksQuery['allDecks'] | undefined>(undefined);

export const areSphericalsVisible = atom<boolean>(true);

export const areNeighbouringImagesVisible = atom<boolean>(false);

export const areDecksVisible = atom<boolean>(false);

export const arePoisVisible = atom<boolean>(false);

export const showOnlySelectedPoi = atom<boolean>(false);

export const snackbarMessage = atom<SnackbarMessage>({ shouldShow: false });

export const globalCallbacks = atom<AbyssViewerCallbacks | undefined>(undefined);

export const cameraTarget = atom<CameraTargetProps | undefined>(undefined);

export const viewerCurrentSpherical = atom<CurrentImageProps | undefined>(undefined);

export const viewerNextCurrentSpherical = atom<CurrentImageProps | undefined>(undefined);

export const selectedAssemblyId = atom<string | undefined>(undefined);

export const selectedAssemblyDetails = atom<GetAssemblyDetailsQuery['assembly'] | undefined>(
  undefined
);

export const selectedAssemblyIds = atom<string[]>([]);

export const unselectedAssemblyIds = atom<string[]>([]);

export const selectedAssemblyName = atom<string | undefined>(undefined);

export const isUncontextualised = atom<boolean>(false);

export const showAssemblyCadModel = atom<boolean>(false);

export const showPhotogrammetryTiles = atom<boolean>(false);

export const showPointCloud = atom<boolean>(true);

export const showPointCloudInSpherical = atom<boolean>(true);

export const showPointsOutsideViewpoint = atom<boolean>(false);

export const pointSize = atom<number>(2);

export const hoveredAssemblyId = atom<string | undefined>(undefined);

export const unitSystem = atom<UnitSystemEnum>(UnitSystemEnum.Imperial);

export const systemTest = atom<boolean>(false);

export const viewpointsByAssemblyId = atom<{ [assemblyId: string]: Viewpoint[] }>({});

export const focalPointsByAssemblyId = atom<{
  [assemblyId: string]: {
    focalPoint?: Point3d;
    cameraPose?: AssemblyCameraPose;
  };
}>({});

export const blisterPolygonVisible = atom<boolean>(true);

export const governingValueByAssemblyId = atom<{
  [assemblyId: string]: {
    blister?: Partial<PointOfInterest> | null;
    calculations?: GoverningValueBlister | null;
    pointOfInterest?: { id: string } | null;
  };
}>({});

export const alarmLevelByAssemblyId = atom<{
  [assemblyId: string]: {
    alarmLevel: AlarmLevels;
    wasAnalysisAlarmLevelManuallySet?: boolean | null;
  };
}>({});

export const allAssemblies = atom<unknown[]>([]);

export const filteredAssemblyIds = atom<string[]>([]);

export const displayAnimationEditor = atom<boolean>(false);

export const selectedStructureDefectsLegends = atom<Array<ToggleableDefectLegend> | undefined>(
  undefined
);

export const nonSelectedDefectsIsEnabled = atom<boolean>(true);

export const allPointOfInterestTemplates = atom<AllPointOfInterestTemplate>([]);

export const allVoiTemplates = atom<string[]>([]);

export const poiTemplateIcons = atom<{ name: string; icon: string; color: string }[]>([]);

export const allAssemblyTemplates = atom<AssemblyTemplatesQuery | undefined>(undefined);

export const assemblyTemplate = atom<AssemblyTemplateQuery | undefined>(undefined);

export const selectedAssemblyTemplateId = atom<string | undefined>(undefined);

export const visitedViewpointIDs = atom<Array<string>>([]);

export const selectedAssemblyInfo = atom<string | undefined>(undefined);

export const drawerLeft = atom<AnalysisDrawerLeft>({
  visibility: false,
  query: true,
});

export const drawerRight = atom<AnalysisDrawerRight>({
  equipment: true,
  aitInsight: false,
  measuringTool: false,
  findViewpointsTool: false,
});

export const drawerRightPreviousState = atom<keyof AnalysisDrawerRight>('equipment');

export const reportsList = atom<AllReportsListQuery['allReports']>([]);

export const filteredReportsList = atom<AllReportsListQuery['allReports']>([]);

export const selectedReport = atom<ReportDetailsQuery['report'] | undefined>(undefined);

export const selectedReportId = atom<string>('');

export const currentVisibilityBox = atom<VisibilityBoxProps>(DEFAULT_VISIBILITY_BOX);

export const selectedTemplateFiltersState = atom<{ id: string; value: string[] }[]>([]);

export const selectedFilteredDeckIds = atom<string[]>([]);

export const selectedFilteredPaintRegionIds = atom<string[]>([]);

export const applyFilters = atom<boolean>(false);

export const refetchUpdatedPois = atom<boolean>(false);

export const selectedReportEquipment = atom<{ id: string; name: string }>({
  id: '',
  name: '',
});

export const showPaintRegionsState = atom<boolean>(false);

export const showCameraPositionsState = atom<boolean>(true);

export const appliedHeatmapValue = atom<string>('');

export const markerScaleSize = atom<number>(2);

export const heatmapSvgState = atom<string>('');

export const paintRegionVisibilityBox = atom<VisibilityBoxProps | undefined>(undefined);

export const allRoles = atom<{ name: string; value: string }[]>([]);

export const paintRegionOutline = atom<string>('');

export const hasRiskAssessment = atom<boolean>(false);

export const selectedFilteredWorkpackIds = atom<string[]>([]);

export const filters = atom<Filters | undefined>(undefined);

export const hasRgb = atom<boolean>(false);

export const structureRelationship = atom<AnalysisStructureRelationships>({
  annotationIdToAssemblyId: {},
  annotationIdToAnnotation3d: {},
  annotation3dToAnnotationId: {},
  assemblyIdToAnnotation3dArray: {},
  assemblyIdAndLocationIdToAnnotation3dArray: {},
  assemblyIdToAssembly3d: {},
  partsByAnnotationId: {},
  parts: {},
  loaded: false,
});

export const blendOpacityCombined = atom<BlendOpacityModes>({
  viewer: 255,
  assembly: 255,
  spherical: 255,
});

export const corrosionOctreeAttributeName = atom<string>('defect');

export const pointOfInterestTemplates = atom<TemplateWithPOIs[]>([]);

export const selectedRiskAssessmentFilters = atom<{ id: string; value: string }[]>([]);

export const riskAssessmentSearchTerm = atom<string>('');

export const riskAssessmentAssemblyTemplate = atom<
  AllAssemblyTemplatesForRiskAssessmentQuery['allTemplates'][0] | undefined
>(undefined);

export const structureTemplate = atom<GetTemplateByTypeQuery['allTemplates'][0] | undefined>(
  undefined
);

export const isTemplateFieldDataUpdated = atom<boolean>(false);

export const maintainSelectedEquipments = atom<boolean>(false);

export const isCriticalityLevelDialogOpen = atom<boolean>(false);

export const assembliesCount = atom<number>(0);

export const allAssembliesForReview =
  atom<GetAllAssembliesForEvaluationQuery['getAssembliesByTemplateFieldsFilters']>();

export const assembliesLoading = atom<boolean | undefined>(undefined);

export const pickingMapsUpdateSequence = atom<number>(0);

export const structureFeatures = atom<GetStructureFeaturesQuery['structure'] | undefined>(
  undefined
);

/**
  Used to filter what kind of paint regions we want to fetch.
 */
export const selectedPaintRegionGroup = atom<PaintRegionGroup | undefined | null>(undefined);

export const isSavedBlocksSelected = atom<boolean>(false);

export const selectedSavedBlockIds = atom<Set<string>>(new Set<string>());

export const chatState = atom<Message[]>([]);

export const chatOpen = atom<boolean>(false);

export const poiCountsByType = atom<Array<{ type: string; count: number }>>([]);

export const insightsExplorerLoadingState = atom<boolean>(false);

export const lastDeeplinkUrlParameters = atom<LastDeeplinkParameters | undefined>(undefined);

export const structureBlisterHeightType = atom<BlisterHeightType | undefined>(undefined);

export const showPointOfInterestAnnotations = atom<boolean>(false);
export const swivelAnnotations = atom<boolean>(false);
export const annotationScale = atom<number>(1);
export const annotationHeightVariance = atom<number>(5);

export const loadingPOIsForReviewReport = atom<boolean>(false);
